import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudRain, CalendarDays } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePeriod, PeriodBar, FranchisePicker, ExportButtons, StatCard, ReportSection, FilterCard } from "./ReportShell";

const HEAD = ["DP Name", "DP ID", "Location", "Franchise", "Date", "Total", "Multi", "Normal", "Rain Orders", "Rain Rate", "Rain Earnings", "Denial", "Cancelled", "Base", "Extra Dist.", "Incentive", "Adjustment", "Deductions", "Final Total"];

export default function DailyReportTab() {
  const per = usePeriod();
  const [fr, setFr] = useState(null);
  const [location, setLocation] = useState("all");
  const [dp, setDp] = useState("all");
  const [locs, setLocs] = useState([]);
  const [dps, setDps] = useState([]);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    api.get("/dp").then(({ data }) => { setDps(data); setLocs([...new Set(data.map((d) => d.location).filter(Boolean))].sort()); }).catch(() => {});
  }, []);

  const params = () => per.buildParams({ franchise_id: fr?.id, location: location === "all" ? "" : location, dp_id: dp === "all" ? "" : dp });
  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/daily?${params()}`); setRep(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate"); } finally { setLoading(false); }
  };
  const download = (fmt, rainOnly = false) => {
    const p = params(); p.set("fmt", fmt); p.set("token", getToken() || "");
    if (rainOnly) p.set("rain_only", "1");
    window.open(`${API}/reports/daily/export?${p}`, "_blank");
  };
  const s = rep?.summary;
  return (
    <div>
      <FilterCard>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FranchisePicker fr={fr} setFr={(v) => { setFr(v); setRep(null); }} />
          <div><Label>Location</Label>
            <Select value={location} onValueChange={setLocation}><SelectTrigger data-testid="daily-report-location"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All locations</SelectItem>{locs.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Delivery Partner</Label>
            <Select value={dp} onValueChange={setDp}><SelectTrigger data-testid="daily-report-dp"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All delivery partners</SelectItem>{dps.map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name} · {d.fe_id}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <PeriodBar {...per} />
        <Button className="mt-4" onClick={generate} disabled={loading} data-testid="generate-daily-report-btn"><CalendarDays className="mr-1.5 h-4 w-4" />{loading ? "Generating…" : "Generate Report"}</Button>
      </FilterCard>
      {s && (
        <div className="space-y-5" data-testid="daily-report-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Daily entries · {s.period_from} to {s.period_to}</p>
            <div className="flex flex-wrap items-center gap-2">
              <ExportButtons onDownload={(f) => download(f)} />
              <Button size="sm" variant="outline" onClick={() => download("csv", true)} data-testid="rain-export-csv"><CloudRain className="mr-1.5 h-3.5 w-3.5 text-sky-500" />Rain CSV</Button>
              <Button size="sm" variant="outline" onClick={() => download("excel", true)} data-testid="rain-export-excel"><CloudRain className="mr-1.5 h-3.5 w-3.5 text-sky-500" />Rain Excel</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard l="Entries" v={num(s.entries)} /><StatCard l="Total Orders" v={num(s.total_orders)} />
            <StatCard l="Total Rain Orders" v={num(s.rain_orders)} accent="primary" /><StatCard l="Rain Surcharge Paid" v={inr(s.rain_earnings)} accent="amber" />
            <StatCard l="Base Earnings" v={inr(s.base_earnings)} /><StatCard l="Deductions" v={inr(s.deductions)} accent="violet" />
            <StatCard l="Final Total Earnings" v={inr(s.total_earnings)} accent="emerald" /><StatCard l="Rain Days" v={num(s.rain_days)} />
          </div>
          <ReportSection title="Daily Delivery Entries">
            <div className="overflow-x-auto">
              <Table><TableHeader><TableRow className="hover:bg-transparent">{HEAD.map((h) => <TableHead key={h} className="whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>{rep.rows.length === 0 ? <TableRow><TableCell colSpan={HEAD.length} className="text-center text-muted-foreground">No entries in this period</TableCell></TableRow> : rep.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`daily-report-row-${i}`}>
                    <TableCell className="whitespace-nowrap font-medium">{r.dp_name}</TableCell><TableCell className="font-mono text-xs">{r.fe_id}</TableCell><TableCell>{r.location}</TableCell><TableCell className="whitespace-nowrap">{r.franchise || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.date}</TableCell><TableCell className="tabular">{r.orders}</TableCell><TableCell className="tabular">{r.multi_orders}</TableCell><TableCell className="tabular">{r.normal_orders}</TableCell>
                    <TableCell className="tabular text-sky-500">{r.rain_orders}</TableCell><TableCell className="tabular">{r.rain_legacy ? "Old flat" : r.rain_rate == null ? "—" : inr(r.rain_rate)}</TableCell>
                    <TableCell className="tabular text-sky-500">{inr(r.rain_earnings)}</TableCell><TableCell className="tabular">{r.denial_orders}</TableCell><TableCell className="tabular">{r.cancelled_orders}</TableCell>
                    <TableCell className="tabular">{inr(r.base_earnings)}</TableCell><TableCell className="tabular">{inr(r.extra_distance)}</TableCell><TableCell className="tabular">{inr(r.other_incentive)}</TableCell><TableCell className="tabular">{inr(r.other_adjustment)}</TableCell>
                    <TableCell className="tabular text-rose-500">{r.deductions ? `− ${inr(r.deductions)}` : "—"}</TableCell><TableCell className="tabular font-semibold text-emerald-500">{inr(r.total)}</TableCell>
                  </TableRow>))}</TableBody></Table>
            </div>
          </ReportSection>
        </div>
      )}
    </div>
  );
}
